import { useRef, useState, useEffect, useCallback } from 'react';
import { keycloak } from '../auth/keycloak';
import { openMfaPopup, MFA_COMPLETE_MESSAGE_TYPE, POPUP_POLL_INTERVAL_MS } from '../utils/popup';
import { MfaSetupOverlay, SUCCESS_DISPLAY_MS } from './MfaSetupOverlay';
import { COLORS, FONTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';

export interface MfaSetupFullPageProps {
  action: string;
  label: string;
  onComplete: () => void;
  onSkip: () => void;
  isDark: boolean;
  colors: ColorTheme;
}

/**
 * MFA / Biometric full-page setup card.
 * Pre-generates a Keycloak action URL and opens it in a popup when the user
 * clicks BEGIN SETUP. Listens for a postMessage callback on completion.
 */
export function MfaSetupFullPage({ action, label, onComplete, onSkip, isDark, colors }: MfaSetupFullPageProps): React.JSX.Element {
  const [view, setView] = useState<'idle' | 'waiting' | 'success'>('idle');
  const [setupUrl, setSetupUrl] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  /** Pre-generate the Keycloak setup URL on mount (async, needs PKCE) */
  useEffect(() => {
    let cancelled = false;
    const callbackUrl = `${window.location.origin}/mfa-callback.html`;
    keycloak.createLoginUrl({ action, redirectUri: callbackUrl })
      .then(url => { if (!cancelled) setSetupUrl(url); });
    return () => { cancelled = true; };
  }, [action]);

  /** Listen for postMessage from mfa-callback.html */
  useEffect(() => {
    function handleMessage(event: MessageEvent): void {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== MFA_COMPLETE_MESSAGE_TYPE) return;
      cleanup();
      setView('success');
      setTimeout(onComplete, SUCCESS_DISPLAY_MS);
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [cleanup, onComplete]);

  /** User clicks BEGIN SETUP -- opens popup (user gesture = popup allowed) */
  function handleBeginSetup(): void {
    if (!setupUrl) return;
    const popup = openMfaPopup(setupUrl);
    if (!popup) return; // Popup still blocked -- don't hijack the main window
    popupRef.current = popup;
    setView('waiting');
    pollRef.current = setInterval(() => {
      if (popup.closed) {
        cleanup();
        setView('idle');
      }
    }, POPUP_POLL_INTERVAL_MS);
  }

  function handleSkip(): void {
    cleanup();
    onSkip();
  }

  const cardShadow = isDark
    ? '4px 4px 12px 0px #0A0A0A, -4px -4px 12px 0px #363636'
    : '6px 6px 20px 0px #DBDBDB, -6px -6px 20px 0px #FFFFFF';

  return (
    <div style={{
      width: 420, padding: 32, borderRadius: 16,
      backgroundColor: colors.card, boxShadow: cardShadow,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
    }}>
      {view === 'success' ? (
        <MfaSetupOverlay view="success" methodLabel={label} onCancel={handleSkip} />
      ) : view === 'waiting' ? (
        <MfaSetupOverlay view="waiting" methodLabel={label} onCancel={handleSkip} />
      ) : (
        /* Idle: show setup prompt with a button the user clicks */
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 20, padding: '48px 24px',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: isDark ? 'rgba(78, 135, 230, 0.15)' : 'rgba(78, 135, 230, 0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={COLORS.accent.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
          </div>
          <p style={{
            fontFamily: FONTS.heading, fontSize: 12, letterSpacing: '0.15em',
            color: colors.text, margin: 0, textAlign: 'center', textTransform: 'uppercase',
          }}>
            Set Up {label}
          </p>
          <p style={{
            fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary,
            margin: 0, textAlign: 'center', lineHeight: '1.5',
          }}>
            A secure window will open to configure your {label.toLowerCase()} authentication.
          </p>
          <button
            onClick={handleBeginSetup}
            disabled={!setupUrl}
            style={{
              width: '100%', padding: '14px 24px', border: 'none', borderRadius: 10,
              background: COLORS.accent.blue, color: '#FFFFFF', cursor: setupUrl ? 'pointer' : 'wait',
              fontFamily: FONTS.heading, fontSize: 12, letterSpacing: '0.15em',
              opacity: setupUrl ? 1 : 0.6,
            }}
          >
            BEGIN SETUP
          </button>
          <button
            onClick={handleSkip}
            style={{
              background: 'none', border: `1px solid ${colors.border}`, borderRadius: 8,
              padding: '8px 24px', cursor: 'pointer', fontFamily: FONTS.heading,
              fontSize: 10, letterSpacing: '0.1em', color: colors.textSecondary,
            }}
          >
            SKIP FOR NOW
          </button>
        </div>
      )}
    </div>
  );
}
